import { getFirestore, doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';

// Cache for user permissions to avoid repeated database calls
const permissionCache = new Map();

export const checkPermission = async (userId, requiredPermission) => {
  try {
    // Check cache first
    const cacheKey = `${userId}-${requiredPermission}`;
    if (permissionCache.has(cacheKey)) {
      return permissionCache.get(cacheKey);
    }

    const db = getFirestore();

    // Find manager document by firebaseUid
    const managersRef = collection(db, 'managers');
    const q = query(managersRef, where('firebaseUid', '==', userId));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      console.log('No manager found with firebaseUid:', userId);
      permissionCache.set(cacheKey, false);
      return false;
    }

    const managerDoc = querySnapshot.docs[0];
    console.log('Found manager document:', managerDoc.id);

    const managerData = managerDoc.data();
    const roleId = managerData.roleId;

    if (!roleId) {
      permissionCache.set(cacheKey, false);
      return false;
    }

    // Get role document
    const roleDoc = await getDoc(doc(db, 'roles', roleId));
    if (!roleDoc.exists()) {
      permissionCache.set(cacheKey, false);
      return false;
    }

    const roleData = roleDoc.data();
    const permissions = roleData.permissions || {};

    // Check if user has the required permission
    const hasPermission = checkPermissionRecursive(permissions, requiredPermission);

    // Cache the result for 5 minutes
    permissionCache.set(cacheKey, hasPermission);
    setTimeout(() => permissionCache.delete(cacheKey), 5 * 60 * 1000);

    return hasPermission;
  } catch (error) {
    console.error('Error checking permission:', error);
    return false;
  }
};

const checkPermissionRecursive = (permissions, requiredPermission) => {
  // Split the permission string (e.g., "quickActions.analyticsHub")
  const parts = requiredPermission.split('.');

  if (parts.length === 1) {
    // Direct permission check
    return permissions[requiredPermission] === true;
  }

  if (parts.length === 2) {
    const [category, permission] = parts;
    return permissions[category]?.[permission] === true;
  }

  return false;
};

export const getUserPermissions = async (userId) => {
  try {
    const db = getFirestore();
    
    console.log('Getting permissions for user ID:', userId);
    
    // Find manager document by firebaseUid
    const managersRef = collection(db, 'managers');
    const q = query(managersRef, where('firebaseUid', '==', userId));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      console.log('No manager found with firebaseUid:', userId);
      return {};
    }

    const managerDoc = querySnapshot.docs[0];
    console.log('Found manager document:', managerDoc.id);

    const managerData = managerDoc.data();
    const roleId = managerData.roleId;

    console.log('Manager data:', managerData);
    console.log('Role ID:', roleId);

    if (!roleId) {
      console.log('No role ID found for manager');
      return {};
    }

    // Get role document
    const roleDoc = await getDoc(doc(db, 'roles', roleId));
    if (!roleDoc.exists()) {
      console.log('Role document not found for ID:', roleId);
      return {};
    }

    const roleData = roleDoc.data();
    console.log('Role data:', roleData);
    console.log('Role permissions:', roleData.permissions);
    
    return roleData.permissions || {};
  } catch (error) {
    console.error('Error getting user permissions:', error);
    return {};
  }
};

export const clearPermissionCache = () => {
  permissionCache.clear();
};



// Permission constants for easy reference - matching dashboard tiles and sidebar menu
export const PERMISSIONS = {
  // Quick Actions permissions
  QUICK_ACTIONS_ANALYTICS_HUB: 'quickActions.analyticsHub',
  QUICK_ACTIONS_ALL_RESERVATIONS: 'quickActions.allReservations',
  QUICK_ACTIONS_TOTAL_EARNINGS: 'quickActions.totalEarnings',
  QUICK_ACTIONS_PENDING_REVIEWS: 'quickActions.pendingReviews',
  QUICK_ACTIONS_UNPAID_INVOICES: 'quickActions.unpaidInvoices',

  // User Management permissions
  USER_MANAGEMENT_PARKING_USERS: 'userManagement.parkingUsers',
  USER_MANAGEMENT_MANUAL_USER_LIST: 'userManagement.manualUserList',
  USER_MANAGEMENT_MANUAL_BUSINESS_LIST: 'userManagement.manualBusinessList',
  USER_MANAGEMENT_PARKING_OWNERS: 'userManagement.parkingOwners',
  USER_MANAGEMENT_PARTNERS: 'userManagement.partners',
  USER_MANAGEMENT_MEAL_VENDORS: 'userManagement.mealVendors',
  USER_MANAGEMENT_MEAL_CUSTOMERS: 'userManagement.mealCustomers',
  USER_MANAGEMENT_ROLE_MANAGEMENT: 'userManagement.roleManagement',

  // Reservations permissions
  RESERVATIONS_RESERVED: 'reservations.reservedReservations',
  RESERVATIONS_CONFIRMED: 'reservations.confirmedReservations',
  RESERVATIONS_ACTIVE: 'reservations.activeReservations',
  RESERVATIONS_COMPLETED: 'reservations.completedReservations',
  RESERVATIONS_CANCELLED: 'reservations.cancelledReservations',

  // Parking Management permissions
  PARKING_MANAGEMENT_TOTAL_SPACES: 'parkingManagement.totalSpaces',
  PARKING_MANAGEMENT_AVAILABLE_SPACES: 'parkingManagement.availableSpaces',
  PARKING_MANAGEMENT_DISABLED_SPACES: 'parkingManagement.disabledSpaces',

  // Financial permissions
  FINANCIAL_TOTAL_TRANSACTIONS: 'financial.totalTransactions',
  FINANCIAL_TOTAL_INVOICES: 'financial.totalInvoices',
  FINANCIAL_TOTAL_EARNINGS: 'financial.totalEarnings',

  // Pass Management permissions
  PASS_MANAGEMENT_TOTAL_PASSES: 'passManagement.totalPasses',
  PASS_MANAGEMENT_MULTI_USE_PASSES: 'passManagement.multiUsePasses',
  PASS_MANAGEMENT_ONE_TIME_PASSES: 'passManagement.oneTimePasses',
  PASS_MANAGEMENT_PARTNER_DISCOUNT_COUPONS: 'passManagement.partnerDiscountCoupons',
  PASS_MANAGEMENT_PERKS_DEAL_VOUCHERS: 'passManagement.perksDealVouchers',

  // Operations permissions
  OPERATIONS_MEAL_DESK_SCHEDULE: 'operations.mealDeskSchedule',
  OPERATIONS_FIELD_DAY_PLANNER: 'operations.fieldDayPlanner',

  // Sidebar Menu permissions
  SIDEBAR_MENU_DASHBOARD: 'sidebarMenu.dashboard',
  SIDEBAR_MENU_USER_MANAGEMENT: 'sidebarMenu.userManagement',
  SIDEBAR_MENU_ANALYTICS: 'sidebarMenu.analytics',
  SIDEBAR_MENU_SETTINGS: 'sidebarMenu.settings',
  SIDEBAR_MENU_NOTIFICATIONS: 'sidebarMenu.notifications'
}; 